/**
 * ExecutionEventStreamRail
 * ─────────────────────────
 * Read-only observational rail for the Execution domain. Renders a
 * compact, most-recent-first stream of canonical execution events with
 * minimal category filters. No writers, no notifications, no actions.
 *
 * Mount: inside ExecutionDomainLanding (xl+ screens only, fixed dock).
 */
import { useEffect, useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { useRole } from "@/data/RoleContext";
import {
  buildExecutionEventStream,
  filterExecutionEvents,
  formatRelativeTime,
  type ExecutionEventCategory,
  type ExecutionEventFilter,
  type ExecutionEvent,
} from "@/data/execution/eventStream";
import {
  SIGNAL_CATEGORIES,
  SIGNAL_KIND_GROUPS,
  SIGNAL_CATEGORY_LABEL,
  SIGNAL_KIND_GROUP_LABEL,
  applySignalPreferences,
  loadSignalPreferences,
  saveSignalPreferences,
  resetSignalPreferences,
  type SignalPreferences,
} from "@/data/execution/signalPreferences";
import { ExecutionSignalClusters } from "@/components/execution/ExecutionSignalClusters";
import { openResolutionDrawer } from "@/data/execution/resolutionDrawerBus";
import { Card } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import {
  Activity,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Wrench,
  PauseCircle,
  ArrowRightLeft,
  Settings2,
  ChevronsRight,
  ChevronsLeft,
  Layers,
  User,
  Briefcase,
  CalendarClock,
} from "lucide-react";
import { useExecutionRailCollapsed } from "./useExecutionRailCollapsed";
import { EXECUTION_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { isExecutionServerPagedRoute, useBrowserLocationSnapshot } from "@/lib/routeScope";
import { useExecutionClusters, useExecutionEventsPage } from "@/hooks/useExecutionServerPaging";
import type { ExecutionSignalClusterRow } from "@/services/execution-page.service";
import type { SignalClusterGroup } from "@/data/execution/signalClusters";

const FILTERS: { id: ExecutionEventFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "qa", label: "QA" },
  { id: "field", label: "Field" },
  { id: "scheduling", label: "Scheduling" },
  { id: "issues", label: "Issues" },
];

const MAX_VISIBLE = 25;
const POLL_MS = 30_000;

const ICON: Record<ExecutionEvent["kind"], React.ComponentType<{ className?: string }>> = {
  callback_created: AlertTriangle,
  holdback_action: PauseCircle,
  field_material_issue: Wrench,
  substitution_request: ArrowRightLeft,
  phase_submitted: Activity,
  phase_qa_passed: CheckCircle2,
  phase_qa_failed: XCircle,
};

const ICON_TONE: Record<ExecutionEventCategory, string> = {
  qa: "text-primary",
  field: "text-warning",
  scheduling: "text-info",
  issues: "text-destructive",
};

export function ExecutionEventStreamRail() {
  const location = useBrowserLocationSnapshot();
  const useServerRail =
    EXECUTION_SERVER_PAGING_ENABLED &&
    isExecutionServerPagedRoute(location.pathname, location.search);
  return useServerRail ? <ServerExecutionEventStreamRail /> : <LegacyExecutionEventStreamRail />;
}

function LegacyExecutionEventStreamRail() {
  const { specExecutions, jobs, callbacks, holdbackLedger } = useAppData();
  const jobNumberById = useMemo(() => {
    const m = new Map<string, string>();
    for (const j of jobs) m.set(j.id, j.jobNumber ?? j.id);
    return m;
  }, [jobs]);
  const vendorPO = useVendorPO();
  const fieldMaterialFeedback = (vendorPO as any).fieldMaterialFeedback ?? [];
  const substitutionRequests = (vendorPO as any).substitutionRequests ?? [];
  const { role } = useRole();
  const [filter, setFilter] = useState<ExecutionEventFilter>("all");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [prefs, setPrefs] = useState<SignalPreferences>(() => loadSignalPreferences(role));
  const [collapsed, setCollapsed] = useExecutionRailCollapsed();

  // Re-load when role changes (defaults differ per role).
  useEffect(() => {
    setPrefs(loadSignalPreferences(role));
  }, [role]);

  const updatePrefs = (next: SignalPreferences) => {
    setPrefs(next);
    saveSignalPreferences(role, next);
  };

  // Lightweight polling for relative-time freshness. No fetches, no subs.
  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), POLL_MS);
    return () => window.clearInterval(t);
  }, []);

  const events = useMemo(
    () =>
      buildExecutionEventStream({
        callbacks,
        holdbackLedger,
        fieldMaterialFeedback: fieldMaterialFeedback ?? [],
        substitutionRequests: substitutionRequests ?? [],
        specExecutions,
      }),
    [callbacks, holdbackLedger, fieldMaterialFeedback, substitutionRequests, specExecutions],
  );

  const preferenceFiltered = useMemo(
    () => applySignalPreferences(events, prefs),
    [events, prefs],
  );

  const filtered = useMemo(
    () => filterExecutionEvents(preferenceFiltered, filter).slice(0, MAX_VISIBLE),
    [preferenceFiltered, filter],
  );

  if (collapsed) {
    return (
      <aside
        data-testid="execution-event-rail"
        data-collapsed="true"
        className="hidden xl:flex fixed right-3 top-36 w-9 flex-col items-center gap-2 z-10"
        aria-label="Execution event stream (collapsed)"
      >
        <button
          type="button"
          data-testid="execution-rail-expand"
          onClick={() => setCollapsed(false)}
          aria-label="Expand Live Stream rail"
          title="Expand Live Stream"
          className="w-9 h-9 inline-flex items-center justify-center rounded-md border border-border/60 bg-card/80 backdrop-blur-sm text-muted-foreground hover:text-foreground hover:bg-muted/70 shadow-sm"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>
        <div
          className="w-9 py-2 flex flex-col items-center gap-1 rounded-md border border-border/40 bg-card/60 backdrop-blur-sm"
          title={`${filtered.length} live event${filtered.length === 1 ? "" : "s"}`}
        >
          <Activity className="h-3.5 w-3.5 text-muted-foreground" />
          <span
            className="text-[10px] font-semibold tabular-nums text-foreground"
            data-testid="event-rail-count"
          >
            {filtered.length}
          </span>
        </div>
      </aside>
    );
  }

  return (
    <aside
      data-testid="execution-event-rail"
      className="hidden xl:flex fixed right-4 top-32 bottom-6 w-72 flex-col gap-2 z-30 pointer-events-none"
      aria-label="Execution event stream"
    >
      <div className="pointer-events-auto">
        <ExecutionSignalClusters />
      </div>
      <Card className="pointer-events-auto flex-1 flex flex-col overflow-hidden border-border/60 shadow-sm">
        <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground flex items-center gap-1.5">
            <Activity className="h-3 w-3" /> Live Stream
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground" data-testid="event-rail-count">
              {filtered.length}
            </span>
            <button
              type="button"
              data-testid="execution-rail-collapse"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse Live Stream rail"
              title="Collapse rail"
              className="h-5 w-5 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <ChevronsRight className="h-3 w-3" />
            </button>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Signal preferences"
                  data-testid="signal-prefs-trigger"
                  className="h-5 w-5 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <Settings2 className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-60 p-3"
                data-testid="signal-prefs-popover"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                    Signal Preferences
                  </p>
                  <button
                    type="button"
                    data-testid="signal-prefs-reset"
                    onClick={() => setPrefs(resetSignalPreferences(role))}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline"
                  >
                    Reset
                  </button>
                </div>
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground">Categories</p>
                  {SIGNAL_CATEGORIES.map((cat) => (
                    <label
                      key={cat}
                      data-testid={`signal-pref-cat-${cat}`}
                      className="flex items-center justify-between text-[11px] py-0.5"
                    >
                      <span>{SIGNAL_CATEGORY_LABEL[cat]}</span>
                      <Switch
                        checked={prefs.categories[cat]}
                        onCheckedChange={(v) =>
                          updatePrefs({
                            ...prefs,
                            categories: { ...prefs.categories, [cat]: v },
                          })
                        }
                      />
                    </label>
                  ))}
                  <div className="border-t border-border/40 my-2" />
                  <p className="text-[10px] font-semibold text-muted-foreground">Event Types</p>
                  {SIGNAL_KIND_GROUPS.map((g) => (
                    <label
                      key={g}
                      data-testid={`signal-pref-kg-${g}`}
                      className="flex items-center justify-between text-[11px] py-0.5"
                    >
                      <span>{SIGNAL_KIND_GROUP_LABEL[g]}</span>
                      <Switch
                        checked={prefs.kindGroups[g]}
                        onCheckedChange={(v) =>
                          updatePrefs({
                            ...prefs,
                            kindGroups: { ...prefs.kindGroups, [g]: v },
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div
          className="px-2 py-1.5 border-b border-border/60 flex gap-1 overflow-x-auto"
          role="tablist"
          aria-label="Filter events"
        >
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`event-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all ${
                  active
                    ? "bg-primary text-primary-foreground ring-1 ring-primary shadow-sm scale-[1.02]"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/70 ring-1 ring-transparent"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <ol className="flex-1 overflow-y-auto divide-y divide-border/40">
          {filtered.length === 0 ? (
            <li className="px-3 py-6 text-center text-[11px] text-muted-foreground">
              No recent events.
            </li>
          ) : (
            filtered.map((e) => {
              const Icon = ICON[e.kind];
              return (
                <li
                  key={e.id}
                  data-testid="event-rail-item"
                  data-category={e.category}
                  className="px-3 py-2 flex gap-2 items-start hover:bg-muted/40 cursor-pointer text-left w-full"
                  onClick={() =>
                    openResolutionDrawer({
                      jobId: e.jobId,
                      source: "live_stream",
                      eventContext: {
                        kind: e.kind,
                        at: e.at,
                        headline: e.headline,
                        detail: e.detail,
                      },
                    })
                  }
                >
                  <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${ICON_TONE[e.category]}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-muted-foreground">
                      {formatRelativeTime(e.at, nowMs)} · {jobNumberById.get(e.jobId) ?? e.jobId}
                    </p>
                    <p className="text-[11px] font-medium truncate">{e.headline}</p>
                    {e.detail && (
                      <p className="text-[10px] text-muted-foreground truncate">{e.detail}</p>
                    )}
                  </div>
                </li>
              );
            })
          )}
        </ol>
      </Card>
    </aside>
  );
}

const GROUP_ICON: Record<SignalClusterGroup, React.ComponentType<{ className?: string }>> = {
  installer: User,
  job: Briefcase,
  scheduling: CalendarClock,
};

const GROUP_LABEL: Record<SignalClusterGroup, string> = {
  installer: "Installer patterns",
  job: "Job patterns",
  scheduling: "Scheduling patterns",
};

function ServerExecutionSignalClusters({ clusters }: { clusters: ExecutionSignalClusterRow[] }) {
  if (clusters.length === 0) return null;

  const groups = (["installer", "job", "scheduling"] as SignalClusterGroup[])
    .map((group) => ({
      key: group,
      items: clusters.filter((cluster) => cluster.group === group),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <Card data-testid="execution-signal-clusters" className="border-border/60 shadow-sm">
      <div className="px-3 py-2 border-b border-border/60 flex items-center gap-1.5">
        <Layers className="h-3 w-3 text-muted-foreground" />
        <h3 className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          Patterns
        </h3>
        <span className="ml-auto text-[10px] text-muted-foreground" data-testid="cluster-total">
          {clusters.length}
        </span>
      </div>
      <div className="px-3 py-2 space-y-2.5">
        {groups.map((group) => {
          const Icon = GROUP_ICON[group.key];
          return (
            <div key={group.key} data-testid={`cluster-group-${group.key}`}>
              <div className="flex items-center gap-1 mb-1 text-[10px] font-semibold text-muted-foreground">
                <Icon className="h-3 w-3" />
                <span>{GROUP_LABEL[group.key]}</span>
              </div>
              <ul className="space-y-0.5">
                {group.items.slice(0, 4).map((cluster) => {
                  const clickable = Boolean(cluster.jobId);
                  return (
                    <li
                      key={cluster.id}
                      data-testid="cluster-item"
                      data-kind={cluster.kind}
                      className={`text-[11px] leading-tight truncate ${clickable ? "cursor-pointer hover:text-primary" : ""}`}
                      title={clickable ? `${cluster.label} - open resolution drawer` : cluster.label}
                      onClick={
                        clickable
                          ? () =>
                              openResolutionDrawer({
                                jobId: cluster.jobId!,
                                source: "signal_cluster",
                                eventContext: {
                                  kind: cluster.kind,
                                  headline: cluster.label,
                                  detail: cluster.detail,
                                },
                              })
                          : undefined
                      }
                    >
                      <span className="text-foreground">{cluster.label}</span>
                      {cluster.detail && <span className="text-muted-foreground"> · {cluster.detail}</span>}
                    </li>
                  );
                })}
                {group.items.length > 4 && (
                  <li className="text-[10px] text-muted-foreground">+{group.items.length - 4} more</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ServerExecutionEventStreamRail() {
  const { role } = useRole();
  const [filter, setFilter] = useState<ExecutionEventFilter>("all");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [prefs, setPrefs] = useState<SignalPreferences>(() => loadSignalPreferences(role));
  const [collapsed, setCollapsed] = useExecutionRailCollapsed();
  const eventsPage = useExecutionEventsPage({
    enabled: true,
    filter,
    page: 0,
    pageSize: MAX_VISIBLE,
  });
  const clusters = useExecutionClusters({ enabled: true });

  useEffect(() => {
    setPrefs(loadSignalPreferences(role));
  }, [role]);

  const updatePrefs = (next: SignalPreferences) => {
    setPrefs(next);
    saveSignalPreferences(role, next);
  };

  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), POLL_MS);
    return () => window.clearInterval(t);
  }, []);

  const events = useMemo(
    () => eventsPage.data.map((event) => ({
      id: event.id,
      kind: event.kind,
      category: event.category,
      jobId: event.jobId,
      at: event.at,
      headline: event.headline,
      detail: event.detail,
      actor: event.actor,
      jobLabel: event.jobLabel,
    })),
    [eventsPage.data],
  );

  const preferenceFiltered = useMemo(
    () => applySignalPreferences(events, prefs),
    [events, prefs],
  );

  const filtered = useMemo(
    () => filterExecutionEvents(preferenceFiltered, filter).slice(0, MAX_VISIBLE),
    [preferenceFiltered, filter],
  );

  if (collapsed) {
    return (
      <aside
        data-testid="execution-event-rail"
        data-collapsed="true"
        className="hidden xl:flex fixed right-3 top-36 w-9 flex-col items-center gap-2 z-10"
        aria-label="Execution event stream (collapsed)"
      >
        <button
          type="button"
          data-testid="execution-rail-expand"
          onClick={() => setCollapsed(false)}
          aria-label="Expand Live Stream rail"
          title="Expand Live Stream"
          className="w-9 h-9 inline-flex items-center justify-center rounded-md border border-border/60 bg-card/80 backdrop-blur-sm text-muted-foreground hover:text-foreground hover:bg-muted/70 shadow-sm"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>
        <div
          className="w-9 py-2 flex flex-col items-center gap-1 rounded-md border border-border/40 bg-card/60 backdrop-blur-sm"
          title={`${filtered.length} live event${filtered.length === 1 ? "" : "s"}`}
        >
          <Activity className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-[10px] font-semibold tabular-nums text-foreground" data-testid="event-rail-count">
            {filtered.length}
          </span>
        </div>
      </aside>
    );
  }

  return (
    <aside
      data-testid="execution-event-rail"
      className="hidden xl:flex fixed right-4 top-32 bottom-6 w-72 flex-col gap-2 z-30 pointer-events-none"
      aria-label="Execution event stream"
    >
      <div className="pointer-events-auto">
        <ServerExecutionSignalClusters clusters={clusters.data} />
      </div>
      <Card className="pointer-events-auto flex-1 flex flex-col overflow-hidden border-border/60 shadow-sm">
        <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground flex items-center gap-1.5">
            <Activity className="h-3 w-3" /> Live Stream
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground" data-testid="event-rail-count">
              {filtered.length}
            </span>
            <button
              type="button"
              data-testid="execution-rail-collapse"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse Live Stream rail"
              title="Collapse rail"
              className="h-5 w-5 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <ChevronsRight className="h-3 w-3" />
            </button>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Signal preferences"
                  data-testid="signal-prefs-trigger"
                  className="h-5 w-5 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <Settings2 className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-60 p-3" data-testid="signal-prefs-popover">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                    Signal Preferences
                  </p>
                  <button
                    type="button"
                    data-testid="signal-prefs-reset"
                    onClick={() => setPrefs(resetSignalPreferences(role))}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline"
                  >
                    Reset
                  </button>
                </div>
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground">Categories</p>
                  {SIGNAL_CATEGORIES.map((cat) => (
                    <label key={cat} data-testid={`signal-pref-cat-${cat}`} className="flex items-center justify-between text-[11px] py-0.5">
                      <span>{SIGNAL_CATEGORY_LABEL[cat]}</span>
                      <Switch
                        checked={prefs.categories[cat]}
                        onCheckedChange={(v) =>
                          updatePrefs({
                            ...prefs,
                            categories: { ...prefs.categories, [cat]: v },
                          })
                        }
                      />
                    </label>
                  ))}
                  <div className="border-t border-border/40 my-2" />
                  <p className="text-[10px] font-semibold text-muted-foreground">Event Types</p>
                  {SIGNAL_KIND_GROUPS.map((g) => (
                    <label key={g} data-testid={`signal-pref-kg-${g}`} className="flex items-center justify-between text-[11px] py-0.5">
                      <span>{SIGNAL_KIND_GROUP_LABEL[g]}</span>
                      <Switch
                        checked={prefs.kindGroups[g]}
                        onCheckedChange={(v) =>
                          updatePrefs({
                            ...prefs,
                            kindGroups: { ...prefs.kindGroups, [g]: v },
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="px-2 py-1.5 border-b border-border/60 flex gap-1 overflow-x-auto" role="tablist" aria-label="Filter events">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`event-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all ${
                  active
                    ? "bg-primary text-primary-foreground ring-1 ring-primary shadow-sm scale-[1.02]"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/70 ring-1 ring-transparent"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <ol className="flex-1 overflow-y-auto divide-y divide-border/40">
          {filtered.length === 0 ? (
            <li className="px-3 py-6 text-center text-[11px] text-muted-foreground">
              {eventsPage.loading ? "Loading recent events..." : "No recent events."}
            </li>
          ) : (
            filtered.map((event) => {
              const Icon = ICON[event.kind];
              const eventWithLabel = event as ExecutionEvent & { jobLabel?: string };
              return (
                <li
                  key={event.id}
                  data-testid="event-rail-item"
                  data-category={event.category}
                  className="px-3 py-2 flex gap-2 items-start hover:bg-muted/40 cursor-pointer text-left w-full"
                  onClick={() =>
                    openResolutionDrawer({
                      jobId: event.jobId,
                      source: "live_stream",
                      eventContext: {
                        kind: event.kind,
                        at: event.at,
                        headline: event.headline,
                        detail: event.detail,
                      },
                    })
                  }
                >
                  <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${ICON_TONE[event.category]}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-muted-foreground">
                      {formatRelativeTime(event.at, nowMs)} · {eventWithLabel.jobLabel ?? event.jobId}
                    </p>
                    <p className="text-[11px] font-medium truncate">{event.headline}</p>
                    {event.detail && <p className="text-[10px] text-muted-foreground truncate">{event.detail}</p>}
                  </div>
                </li>
              );
            })
          )}
        </ol>
      </Card>
    </aside>
  );
}

export default ExecutionEventStreamRail;
